import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoinListItem } from '../../types';
import { useCoinList } from '../../hooks/useCoinList';
import { cgGet } from '../../utils/coingecko';
import { CoinSelector } from '../Transactions/CoinSelector';
import { CardHeader } from '../ui/Card';
import { useStrategy } from '../../context/StrategyContext';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { useTaxPlan } from '../../hooks/useTaxPlan';
import { formatDate } from '../../utils/formatters';
import { costBasisByCoin } from '../../utils/calculations';
import { buildDisplayRateTable } from '../../utils/displayRates';
import { AlertTriangle, TrendingDown, Receipt, Info, Loader2, Bookmark, Check, ArrowRight } from 'lucide-react';

// ── Australian CGT tax brackets (FY 2024–25) ──────────────────────────────────
const TAX_BRACKETS = [
  { label: '$0 – $18,200',        rate: 0      },
  { label: '$18,201 – $45,000',   rate: 0.19   },
  { label: '$45,001 – $120,000',  rate: 0.325  },
  { label: '$120,001 – $180,000', rate: 0.37   },
  { label: '$180,001+',           rate: 0.45   },
];

const SELECT_CLASS =
  'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors cursor-pointer [color-scheme:dark]';

function fmtAUD(amount: number, opts?: { decimals?: number; sign?: boolean }): string {
  const d   = opts?.decimals ?? (Math.abs(amount) < 1 ? 6 : 2);
  const str = new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD',
    minimumFractionDigits: d, maximumFractionDigits: d,
  }).format(Math.abs(amount));
  if (opts?.sign && amount > 0) return `+${str}`;
  if (amount < 0)               return `−${str}`;
  return str;
}

function getLongTermInfo(purchaseDate: string) {
  if (!purchaseDate) return { isLongTerm: false, daysHeld: 0, daysRemaining: 0 };
  const purchase  = new Date(purchaseDate);
  const today     = new Date();
  purchase.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const threshold = new Date(purchase);
  threshold.setFullYear(threshold.getFullYear() + 1);
  const isLongTerm    = today >= threshold;
  const daysHeld      = Math.max(0, Math.floor((today.getTime() - purchase.getTime()) / 86_400_000));
  const daysRemaining = isLongTerm ? 0 : Math.ceil((threshold.getTime() - today.getTime()) / 86_400_000);
  return { isLongTerm, daysHeld, daysRemaining };
}

interface Props {
  embedded?: { coin: CoinListItem };
  /** Hosted in the Tax tab's Planned Sales panel: coin picker + Save, clean form. */
  hosted?: boolean;
  /** Hosted mode: preselect this coin (e.g. from a harvest "Add to plan" click). */
  initialCoinId?: string;
}

function isTaxEmpty(coinsSold: string, buyPrice: string, sellPrice: string, purchaseDate: string): boolean {
  return coinsSold === '' && buyPrice === '' && sellPrice === '' && purchaseDate === '';
}

export function TaxHarvestEstimator({ embedded, hosted, initialCoinId }: Props = {}) {
  const { coins, loading: coinsLoading, error: coinsError } = useCoinList();
  const { taxHarvest, updateTaxHarvest } = useStrategy();
  const { holdings, transactions } = usePortfolioContext();
  const { addEstimate } = useTaxPlan();
  const navigate = useNavigate();

  // Save-to-Tax-Plan UI state (embedded mode only)
  const [scenarioName, setScenarioName] = useState('');
  const [savedToast,   setSavedToast]   = useState(false);

  // When embedded, only seed from saved tax state if it's for this coin.
  // Hosted "Add estimate" always starts as a clean form.
  const seed = embedded
    ? (taxHarvest?.coinId === embedded.coin.id ? taxHarvest : null)
    : hosted
      ? null
      : taxHarvest;

  const [selectedCoin,     setSelectedCoin]     = useState<CoinListItem | null>(embedded ? embedded.coin : null);
  const [livePriceAud,     setLivePriceAud]     = useState<number | null>(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [coinsSold,        setCoinsSold]        = useState(seed?.coinsSold    ?? '');
  const [buyPrice,         setBuyPrice]         = useState(seed?.buyPrice     ?? '');
  const [sellPrice,        setSellPrice]        = useState(seed?.sellPrice    ?? '');
  const [purchaseDate,     setPurchaseDate]     = useState(seed?.purchaseDate ?? '');
  const [bracketIdx,       setBracketIdx]       = useState(seed?.bracketIdx   ?? 2);

  // Fetch AUD live price when coin changes (CoinGecko supports vs_currencies=aud)
  useEffect(() => {
    if (!selectedCoin) { setLivePriceAud(null); return; }

    let cancelled = false;
    setLivePriceLoading(true);
    cgGet('/simple/price', {
      params: { ids: selectedCoin.id, vs_currencies: 'aud' },
      timeout: 8_000,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const price = (data as Record<string, { aud: number }>)[selectedCoin.id]?.aud ?? 0;
        setLivePriceAud(price > 0 ? price : null);
      })
      .catch(() => { if (!cancelled) setLivePriceAud(null); })
      .finally(() => { if (!cancelled) setLivePriceLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCoin]);

  const handleCoinSelect = useCallback((coin: CoinListItem) => setSelectedCoin(coin), []);

  // ── Restore (non-embedded) once coins list loads ──────────────────────────
  const restoredCoinRef = useRef(false);
  useEffect(() => {
    if (embedded || hosted || restoredCoinRef.current || !coins.length || !taxHarvest?.coinId) return;
    restoredCoinRef.current = true;
    const coin = coins.find(c => c.id === taxHarvest.coinId) ?? null;
    if (coin) setSelectedCoin(coin);
  }, [coins]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Embedded: react to coin route changes & auto-fill from portfolio ─────
  const portfolioFilledRef = useRef(false);
  useEffect(() => {
    if (!embedded) return;
    if (selectedCoin?.id !== embedded.coin.id) {
      setSelectedCoin(embedded.coin);
      portfolioFilledRef.current = false;
      // Reset to saved values if this coin matches the saved scenario, else clear
      if (taxHarvest?.coinId === embedded.coin.id) {
        setCoinsSold(taxHarvest.coinsSold);
        setBuyPrice(taxHarvest.buyPrice);
        setSellPrice(taxHarvest.sellPrice);
        setPurchaseDate(taxHarvest.purchaseDate);
        setBracketIdx(taxHarvest.bracketIdx);
      } else {
        setCoinsSold('');
        setBuyPrice('');
        setSellPrice('');
        setPurchaseDate('');
      }
    }
  }, [embedded?.coin.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once we have the live AUD price (and no saved scenario for this coin),
  // pre-fill coins sold + buy/sell prices + earliest purchase date from portfolio
  useEffect(() => {
    if (!embedded || portfolioFilledRef.current) return;
    if (taxHarvest?.coinId === embedded.coin.id) { portfolioFilledRef.current = true; return; }
    if (livePriceAud == null || embedded.coin.currentPrice <= 0) return;

    const holding = holdings.find(h => h.coinId === embedded.coin.id);
    if (!holding) { portfolioFilledRef.current = true; return; }

    const usdToAud = livePriceAud / embedded.coin.currentPrice;
    const buyAud   = audCostPerUnit(embedded.coin.id, usdToAud) ?? holding.avgBuyPrice * usdToAud;

    // Earliest BUY transaction date for this coin
    const firstBuy = transactions
      .filter(t => t.coinId === embedded.coin.id && t.type === 'BUY')
      .sort((a, b) => a.timestamp - b.timestamp)[0];

    setCoinsSold(holding.amount.toString());
    setBuyPrice(buyAud.toFixed(buyAud < 1 ? 6 : 2));
    setSellPrice(livePriceAud.toFixed(livePriceAud < 1 ? 6 : 2));
    if (firstBuy?.date) setPurchaseDate(firstBuy.date);
    portfolioFilledRef.current = true;
  }, [embedded, livePriceAud, holdings, transactions, taxHarvest]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The holding's average cost per unit in AUD, with each BUY converted at ITS OWN
   * date's rate.
   *
   * Not `holding.avgBuyPrice * usdToAud`: that re-converts a cost paid years ago at
   * today's rate, which is the error the rest of the app just stopped making. It
   * also would not match the Realised CGT tab, which converts per leg — so the two
   * tax views would disagree about the cost base of the same coins.
   *
   * AUD specifically, not the display currency: this is an Australian CGT estimate.
   */
  const audCostPerUnit = useCallback(
    (coinId: string, usdToAud: number): number | null => {
      const table = buildDisplayRateTable('AUD', usdToAud);
      const basis = costBasisByCoin(transactions, table)[coinId];
      if (!basis || basis.amount <= 0) return null;
      return basis.totalCostDisplay / basis.amount;
    },
    [transactions]
  );

  // ── Hosted (Planned Sales): preselect the passed-in coin, then prefill ────────
  useEffect(() => {
    if (!hosted || !initialCoinId || !coins.length) return;
    if (selectedCoin?.id === initialCoinId) return;
    const coin = coins.find((c) => c.id === initialCoinId);
    if (coin) setSelectedCoin(coin);
  }, [hosted, initialCoinId, coins]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill the live AUD price as the sell price when a coin is chosen (mirrors the
  // coin page). If the user already holds it, also prefill quantity / avg buy /
  // first-buy date from their portfolio.
  const hostedFilledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hosted || !selectedCoin || livePriceAud == null) return;
    if (hostedFilledRef.current === selectedCoin.id) return;
    hostedFilledRef.current = selectedCoin.id;

    setSellPrice(livePriceAud.toFixed(livePriceAud < 1 ? 6 : 2));
    const holding  = holdings.find((h) => h.coinId === selectedCoin.id);
    const usdToAud = selectedCoin.currentPrice > 0 ? livePriceAud / selectedCoin.currentPrice : 0;
    if (holding && usdToAud > 0) {
      const buyAud = audCostPerUnit(selectedCoin.id, usdToAud) ?? holding.avgBuyPrice * usdToAud;
      setCoinsSold(holding.amount.toString());
      setBuyPrice(buyAud.toFixed(buyAud < 1 ? 6 : 2));
      const firstBuy = transactions
        .filter((t) => t.coinId === selectedCoin.id && t.type === 'BUY')
        .sort((a, b) => a.timestamp - b.timestamp)[0];
      if (firstBuy?.date) setPurchaseDate(firstBuy.date);
    }
  }, [hosted, selectedCoin, livePriceAud, holdings, transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to context (skip auto-overwrite of other coin's empty scenario) ──
  useEffect(() => {
    if (!selectedCoin || hosted) return; // hosted is a self-contained add form — no draft persistence
    if (isTaxEmpty(coinsSold, buyPrice, sellPrice, purchaseDate) && taxHarvest && taxHarvest.coinId !== selectedCoin.id) return;
    updateTaxHarvest({
      coinId:       selectedCoin.id,
      coinName:     selectedCoin.name,
      coinSymbol:   selectedCoin.symbol,
      coinImage:    selectedCoin.image ?? '',
      coinsSold,
      buyPrice,
      sellPrice,
      purchaseDate,
      bracketIdx,
    });
  }, [selectedCoin?.id, coinsSold, buyPrice, sellPrice, purchaseDate, bracketIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parsed values ──────────────────────────────────────────────────────────
  const coinsNum   = parseFloat(coinsSold) || 0;
  const buyNum     = parseFloat(buyPrice)  || 0;
  const sellNum    = parseFloat(sellPrice) || 0;
  const bracket    = TAX_BRACKETS[bracketIdx];
  const { isLongTerm, daysHeld, daysRemaining } = getLongTermInfo(purchaseDate);

  const totalGain       = (sellNum - buyNum) * coinsNum;
  const isGain          = totalGain > 0;
  const isLoss          = totalGain < 0;
  const discountAmount  = isGain && isLongTerm ? totalGain * 0.5 : 0;
  const taxableGain     = isGain ? totalGain - discountAmount : 0;
  const taxOwed         = taxableGain * bracket.rate;
  const netProfit       = totalGain - taxOwed;
  const effectiveRate   = isGain && totalGain > 0 ? (taxOwed / totalGain) * 100 : 0;
  const canCalculate    = coinsNum > 0 && buyNum > 0 && sellNum > 0 && !!purchaseDate;
  const today           = new Date().toISOString().split('T')[0];

  const inputClass = 'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors';
  const labelClass = 'block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider';
  const dash       = <span className="text-slate-600">—</span>;

  // ── Save a frozen snapshot of the current estimate to the Tax Plan ──────────
  const handleSaveEstimate = useCallback(() => {
    if (!selectedCoin || !canCalculate) return;
    const monthsHeld = Math.round(daysHeld / 30.436875);
    addEstimate({
      coinId:             selectedCoin.id,
      coinName:           selectedCoin.name,
      coinTicker:         selectedCoin.symbol.toUpperCase(),
      coinImage:          selectedCoin.image ?? '',
      amountToSell:       coinsNum,
      costBasis:          buyNum,
      estimatedSalePrice: sellNum,
      holdDurationMonths: monthsHeld,
      cgtDiscountApplied: isLongTerm,
      capitalGain:        totalGain,
      taxBracket:         bracket.label,
      taxBracketRate:     bracket.rate,
      estimatedCGT:       taxOwed,
      scenarioName:       scenarioName.trim() || `Saved ${formatDate(new Date().toISOString())}`,
    });
    setScenarioName('');
    setSavedToast(true);
  }, [selectedCoin, canCalculate, daysHeld, coinsNum, buyNum, sellNum, isLongTerm, totalGain, bracket, taxOwed, scenarioName, addEstimate]);

  // Auto-dismiss the confirmation toast.
  useEffect(() => {
    if (!savedToast) return;
    const t = setTimeout(() => setSavedToast(false), 6000);
    return () => clearTimeout(t);
  }, [savedToast]);

  const inner = (
    <>

      {/* Live AUD price banner when embedded — coin selector is hidden */}
      {embedded && (livePriceAud != null || livePriceLoading) && (
        <div className="text-xs text-slate-500">
          {livePriceLoading
            ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Fetching AUD price…</span>
            : <>Live {selectedCoin?.symbol}: <span className="text-slate-300 font-mono">{fmtAUD(livePriceAud!)}</span></>}
        </div>
      )}

      {/* ── Inputs ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

        {/* Asset selector — hidden in embedded mode */}
        {!embedded && (
          <div className="sm:col-span-2 lg:col-span-1">
            <label className={labelClass}>Asset</label>
            <CoinSelector
              coins={coins}
              selectedId={selectedCoin?.id ?? ''}
              onSelect={handleCoinSelect}
              loading={coinsLoading}
              error={coinsError}
            />
            <div className="mt-2 h-4 flex items-center">
              {selectedCoin && (
                livePriceLoading
                  ? <span className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Fetching AUD price…</span>
                  : livePriceAud != null
                    ? <span className="text-xs text-slate-500">Live: <span className="text-slate-300 font-mono">{fmtAUD(livePriceAud)}</span></span>
                    : null
              )}
            </div>
          </div>
        )}

        <div>
          <label className={labelClass}>Coins Sold</label>
          <input
            type="number" min="0"
            value={coinsSold}
            onChange={e => setCoinsSold(e.target.value)}
            placeholder="e.g. 0.5"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Purchase Date</label>
          <input
            type="date"
            value={purchaseDate}
            onChange={e => setPurchaseDate(e.target.value)}
            max={today}
            className={inputClass}
          />
          {purchaseDate && (
            <p className="mt-1.5 text-xs">
              {isLongTerm
                ? <span className="text-emerald-400">✓ Long-term - {daysHeld} days held (50% CGT discount)</span>
                : <span className="text-amber-400">{daysHeld}d held - {daysRemaining}d until long-term discount</span>}
            </p>
          )}
        </div>

        <div>
          <label className={labelClass}>Buy Price (AUD)</label>
          <input
            type="number" min="0"
            value={buyPrice}
            onChange={e => setBuyPrice(e.target.value)}
            placeholder="e.g. 50000"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Sell Price (AUD)</label>
          <input
            type="number" min="0"
            value={sellPrice}
            onChange={e => setSellPrice(e.target.value)}
            placeholder="e.g. 80000"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Annual Income Bracket</label>
          <select
            value={bracketIdx}
            onChange={e => setBracketIdx(Number(e.target.value))}
            className={SELECT_CLASS}
          >
            {TAX_BRACKETS.map((b, i) => (
              <option key={i} value={i}>
                {b.label} - {(b.rate * 100).toFixed(0)}% tax rate
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-slate-500">Marginal rate on capital gains</p>
        </div>

      </div>

      {/* ── Tax loss harvesting callout ────────────────────────────────────── */}
      {canCalculate && isLoss && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <TrendingDown className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-300">Tax Loss Harvesting Opportunity</p>
            <p className="text-sm text-amber-200/70 leading-relaxed">
              This sale realises a capital loss of{' '}
              <span className="font-semibold text-amber-300">{fmtAUD(Math.abs(totalGain))}</span>.
              This loss can be offset against other capital gains in the same financial year,
              reducing your overall CGT liability. Any unused loss carries forward to future years.
            </p>
          </div>
        </div>
      )}

      {/* ── Validation nudge ───────────────────────────────────────────────── */}
      {!canCalculate && (coinsSold || buyPrice || sellPrice || purchaseDate) && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-900/40 border border-slate-700/50">
          <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <p className="text-sm text-slate-500">Fill in all fields above to see your estimate.</p>
        </div>
      )}

      {/* ── Summary card ───────────────────────────────────────────────────── */}
      {canCalculate && (
        <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-5 space-y-5">

          {/* Card header with long/short badge */}
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg border ${
              isGain ? 'bg-emerald-500/10 border-emerald-500/20'
            : isLoss ? 'bg-red-500/10 border-red-500/20'
            :          'bg-slate-700/50 border-slate-600/50'}`}>
              <Receipt className={`w-4 h-4 ${isGain ? 'text-emerald-400' : isLoss ? 'text-red-400' : 'text-slate-400'}`} />
            </div>
            <p className="text-sm font-semibold text-slate-100">CGT Summary</p>
            <span className={`ml-auto text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
              isLongTerm
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-amber-500/10  text-amber-400  border-amber-500/20'}`}>
              {isLongTerm ? '12+ month hold' : 'Under 12 months'}
            </span>
          </div>

          {/* Six-cell stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">

            <div>
              <p className="text-xs text-slate-500 mb-1">Gross Gain / Loss</p>
              <p className={`text-base font-bold font-display ${isGain ? 'text-emerald-400' : isLoss ? 'text-red-400' : 'text-slate-100'}`}>
                {fmtAUD(totalGain, { sign: true })}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {coinsNum} {selectedCoin?.symbol ?? 'coins'}
              </p>
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1">CGT Discount</p>
              {isGain && isLongTerm ? (
                <>
                  <p className="text-base font-bold font-display text-emerald-400">−{fmtAUD(discountAmount)}</p>
                  <p className="text-xs text-emerald-500/70 mt-0.5">50% discount applied</p>
                </>
              ) : isGain ? (
                <>
                  <p className="text-base font-bold font-display text-slate-500">{fmtAUD(0)}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Not eligible yet</p>
                </>
              ) : (
                <p className="text-base font-bold font-display text-slate-600">{dash}</p>
              )}
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1">Taxable Gain</p>
              <p className={`text-base font-bold font-display ${taxableGain > 0 ? 'text-slate-100' : 'text-slate-500'}`}>
                {isGain ? fmtAUD(taxableGain) : dash}
              </p>
              {isGain && isLongTerm && (
                <p className="text-xs text-slate-500 mt-0.5">after 50% discount</p>
              )}
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1">Est. Tax Owed</p>
              <p className={`text-base font-bold font-display ${taxOwed > 0 ? 'text-red-400' : 'text-slate-500'}`}>
                {isGain ? (taxOwed > 0 ? `−${fmtAUD(taxOwed)}` : fmtAUD(0)) : dash}
              </p>
              {isGain && (
                <p className="text-xs text-slate-500 mt-0.5">at {(bracket.rate * 100).toFixed(0)}% marginal rate</p>
              )}
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1">Net Profit After Tax</p>
              <p className={`text-base font-bold font-display ${netProfit > 0 ? 'text-emerald-400' : netProfit < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                {fmtAUD(netProfit, { sign: true })}
              </p>
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1">Effective Tax Rate</p>
              <p className={`text-base font-bold font-display ${effectiveRate > 0 ? 'text-slate-100' : 'text-slate-500'}`}>
                {isGain ? `${effectiveRate.toFixed(1)}%` : dash}
              </p>
              {isGain && isLongTerm && (
                <p className="text-xs text-slate-500 mt-0.5">vs {(bracket.rate * 100).toFixed(0)}% without discount</p>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── Disclaimer ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-slate-900/40 border border-slate-700/50">
        <Info className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 leading-relaxed">
          This is an estimate only and does not constitute financial or tax advice. Calculations use
          simplified ATO CGT rules and may not reflect your complete tax position. Please consult a
          registered tax agent or financial advisor for personalised advice.
        </p>
      </div>

      {/* ── Save to Tax Plan (embedded coin Tax tab, or hosted in Planned Sales) ── */}
      {(embedded || hosted) && (
        <div className="pt-1 border-t border-slate-700/40 space-y-3">
          <div>
            <label className={labelClass}>Scenario name (optional)</label>
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="e.g. Take profits at $150k"
              maxLength={80}
              className={inputClass}
            />
          </div>
          <button
            type="button"
            onClick={handleSaveEstimate}
            disabled={!canCalculate}
            title={canCalculate ? 'Save this estimate to your Tax Plan' : 'Fill in all fields to save'}
            className="inline-flex items-center gap-2 transition-[filter,opacity]"
            style={{
              backgroundColor: '#d4b483',
              color: '#0c0e12',
              padding: '10px 20px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              opacity: canCalculate ? 1 : 0.4,
              cursor: canCalculate ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => { if (canCalculate) e.currentTarget.style.filter = 'brightness(1.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
          >
            <Bookmark className="w-4 h-4" />
            Save estimate to Tax Plan
          </button>
        </div>
      )}
    </>
  );

  if (embedded || hosted) {
    return (
      <div className="space-y-6">
        {inner}

        {/* Confirmation toast */}
        {savedToast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/30">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            </span>
            <p className="text-sm text-slate-100 font-medium">Estimate saved to Tax Plan</p>
            {/* Hosted view already lives in the Tax Plan, so the "View plan" jump is
                only useful from the embedded (coin page) context. */}
            {!hosted && (
              <button
                type="button"
                onClick={() => navigate({ pathname: '/app', search: '?tab=tax' }, { state: { scrollToTaxPlan: true } })}
                className="inline-flex items-center gap-1 text-sm font-semibold ml-1"
                style={{ color: '#d4b483' }}
              >
                View plan
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-slate-800/40 border border-slate-700 p-6 space-y-6">
      <CardHeader
        title="Tax Harvest Estimator"
        subtitle="Australian CGT estimate - short vs long-term hold"
      />
      {inner}
    </div>
  );
}
